// src/components/shell/BucketSwitchToast.tsx
// Doctrine A8: brief 3.5s pill ("Now in [Bucket]") confirming that the user has
// just moved into a different use-case bucket. Watches the derived active bucket
// from resolveActiveBucket(mode, auth) and only fires when that id changes from
// a previous non-null value, so:
//   - Initial mount does not fire.
//   - Mode switches inside the same bucket do not fire.
//   - Single-bucket users (whose active bucket id is stable) never see it.
// The A7 lateral-slide bucket transition animation is intentionally out of
// scope for this slice.
import { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useAuth } from "@/contexts/AuthContext";
import { useAnchorEntitlements } from "@/contexts/AnchorEntitlementsContext";
import {
  getBucketById,
  resolveActiveBucket,
} from "@/components/shell/modes/bucketsRegistry";
import type { PrimaryMode } from "@/components/lead/sections/LeadWorkspaceHeaderAccess";

export type BucketSwitchToastProps = {
  mode: PrimaryMode;
};

const VISIBLE_MS = 3500;

export function BucketSwitchToast({ mode }: BucketSwitchToastProps) {
  const auth = useAuth();
  const { anchors } = useAnchorEntitlements();
  const activeBucketId = resolveActiveBucket(mode, auth, anchors);

  const prevBucketIdRef = useRef<string | null>(null);
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [visibleLabel, setVisibleLabel] = useState<string | null>(null);

  useEffect(() => {
    const prev = prevBucketIdRef.current;
    prevBucketIdRef.current = activeBucketId;

    // Skip initial mount (no previous bucket) and the case where the resolver
    // briefly returns null while auth state is still loading.
    if (prev === null || activeBucketId === null) return;
    if (prev === activeBucketId) return;

    const entry = getBucketById(activeBucketId);
    if (!entry) return;

    if (timeoutRef.current !== null) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
    setVisibleLabel(entry.label);
    timeoutRef.current = setTimeout(() => {
      setVisibleLabel(null);
      timeoutRef.current = null;
    }, VISIBLE_MS);
  }, [activeBucketId]);

  useEffect(() => {
    return () => {
      if (timeoutRef.current !== null) {
        clearTimeout(timeoutRef.current);
        timeoutRef.current = null;
      }
    };
  }, []);

  if (typeof document === "undefined") return null;
  if (!visibleLabel) return null;

  return createPortal(
    <div
      className="pointer-events-none fixed inset-x-0 bottom-4 z-50 flex justify-center px-4"
      aria-hidden={false}
    >
      <div
        role="status"
        aria-live="polite"
        className="pointer-events-auto rounded-full bg-indigo-600 px-4 py-2 text-sm font-semibold text-white shadow-lg ring-1 ring-indigo-700/30"
      >
        Now in {visibleLabel}
      </div>
    </div>,
    document.body
  );
}
